/**
 * A challenge's board (lane ALGOBOO2, CHALLENGE1): each agent's week side by side -- its result, what it paid in gas
 * and fees, its worst drop, the days it sat out and its return -- in the order of the platform's one ranking rule (its
 * standings, lane BOOCLEAN1), the unranked last with the reason. Paper results say "simulated". Anyone with the link can see it. The conditions every agent is compared
 * under are published beside it -- the same amount and week, how sizes and costs count, the worst drop's basis -- and
 * what is not done is said by name (lane BOOUX1, A2-14, A2-16).
 */
import { createFileRoute } from '@tanstack/react-router'
import type { ChallengeScore } from '@algoboo/schema/api'
import { Card, EmptyState, Pill, Skeleton, UiLink, formatCount, formatEth, fullTime, paths } from '@algoboo/ui'
import { challengeQuery, useChallenge } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { LimitNote } from '../components/LimitNote'
import { QueryError } from '../components/widgets'
import { PHASE_WORDS } from './challenges.index'
import { prefetch } from '../lib/route-data'

export const Route = createFileRoute('/challenges/$id')({
  loader: ({ context: { queryClient: qc }, params: { id } }) => prefetch(qc.ensureQueryData(challengeQuery(id))),
  component: ChallengePage,
})

const eth = (v: string | null) => (v === null ? 'not known' : formatEth(v))

function ChallengePage() {
  const { id } = Route.useParams()
  const q = useChallenge(id)
  usePageMeta(q.data ? q.data.title : 'Challenge')
  if (q.isLoading) return <Skeleton className="h-64 rounded-xl" />
  if (q.error || !q.data) return <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
  const c = q.data
  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader
        title={c.title}
        lede={
          <>
            {formatEth(c.budgetEth)} ETH of play money · {fullTime(c.startsAt)} to {fullTime(c.endsAt)} · {PHASE_WORDS[c.phase]}
            {c.createdBy.handle ? ` · started by @${c.createdBy.handle}` : ''}
          </>
        }
      />
      {c.simulated && (
        <p className="text-[12.5px] text-ink-3">
          <Pill tone="warn">simulated</Pill> Results on paper are the platform’s fill model, not trades on chain.
        </p>
      )}
      {c.scores.length === 0 ? (
        <EmptyState title="No agents yet" body="Agents join until the week starts." />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[640px] text-[13px]" data-testid="challenge-board">
            <thead className="text-left text-[11.5px] text-ink-3">
              <tr>
                <th className="p-3">#</th>
                <th className="p-3">Agent</th>
                <th className="p-3 text-right">Return</th>
                <th className="p-3 text-right">On the play money</th>
                <th className="p-3 text-right">Result</th>
                <th className="p-3 text-right">Gas and fees</th>
                <th className="p-3 text-right">Worst drop</th>
                <th className="p-3 text-right">Days sat out</th>
                <th className="p-3 text-right">Trades</th>
              </tr>
            </thead>
            <tbody>
              {c.scores.map((s) => (
                <Row key={s.agent.id} s={s} />
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <p className="text-[12px] text-ink-3" data-testid="challenge-rank-rule">
        {c.rankRule}
        {c.standingsAsOf ? ` The platform stated these standings at ${fullTime(c.standingsAsOf)}.` : ''}
      </p>
      <p className="text-[12px] text-ink-3">{c.rules}</p>
      <Card className="space-y-1.5 p-4" data-testid="challenge-conditions">
        <h2 className="text-[13px] font-semibold">The same conditions for every agent</h2>
        <p className="text-[12px] text-ink-2">
          {formatEth(c.conditions.budgetEth)} of play money, {fullTime(c.conditions.startsAt)} to {fullTime(c.conditions.endsAt)}.
        </p>
        <p className="text-[12px] text-ink-2">{c.conditions.sizing}</p>
        <p className="text-[12px] text-ink-2">{c.conditions.costs}</p>
        <LimitNote limit={c.conditions.intraday} />
        <LimitNote limit={c.conditions.sharedAccount} />
      </Card>
      {c.asOf && <p className="text-[12px] text-ink-3">Read {fullTime(c.asOf)}.</p>}
    </div>
  )
}

function Row({ s }: { s: ChallengeScore }) {
  return (
    <tr className="border-t border-line align-top" data-testid="challenge-row" data-handle={s.agent.handle}>
      <td className="p-3">{s.rank ?? '–'}</td>
      <td className="p-3">
        <UiLink href={paths.agent(s.agent.handle)} className="font-medium hover:underline">
          {s.agent.name}
        </UiLink>
        <span className="block text-[11.5px] text-ink-3">
          @{s.agent.handle} · {s.book === 'live' ? 'real money' : 'play money'}
        </span>
        {s.rankWords && <span className="block text-[11.5px] text-ink-3">{s.rankWords}</span>}
        {s.unscored && <span className="block text-[11.5px] text-ink-3">{s.unscored}</span>}
      </td>
      <td className="p-3 text-right">{s.returnPct === null ? '–' : `${s.returnPct}%`}</td>
      <td className="p-3 text-right">{s.onBudgetEth === null ? '–' : formatEth(s.onBudgetEth, { sign: 'always' })}</td>
      <td className="p-3 text-right">{s.netEth === null ? (s.decisions === null ? '–' : 'not known') : formatEth(s.netEth, { sign: 'always' })}</td>
      <td className="p-3 text-right">{s.gasEth === null ? '–' : `${eth(s.gasEth)} gas · ${eth(s.feesEth)} fees`}</td>
      <td className="p-3 text-right">{s.drawdownEth === null ? '–' : formatEth(s.drawdownEth)}</td>
      <td className="p-3 text-right">{s.daysSatOut === null ? '–' : formatCount(s.daysSatOut)}</td>
      <td className="p-3 text-right">{s.filled === null ? '–' : formatCount(s.filled)}</td>
    </tr>
  )
}
